import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import SignatureUpload from "@/components/SignatureUpload";
import { Plus, Edit, Trash2, Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, Search, Eye, EyeOff, Copy } from "lucide-react";

const empty = { code: "", full_name: "", phone: "", email: "", username: "", is_active: true, password: "" };

export default function AdminTechnicians() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [revealPw, setRevealPw] = useState({});
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    // Import state
    const [showImport, setShowImport] = useState(false);
    const [importPreview, setImportPreview] = useState(null);
    const [importFile, setImportFile] = useState(null);
    const [importLoading, setImportLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const importRef = useRef(null);

    const load = () => api.get("/technicians?include_inactive=true").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const toggleActive = async (t) => {
        try {
            await api.patch(`/technicians/${t.id}/active?is_active=${!t.is_active}`);
            toast.success(!t.is_active ? "Técnico activado" : "Técnico desactivado");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };

    const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
    const openEdit = (t) => { setEditing(t); setForm({ ...empty, ...t, phone: t.phone || "", email: t.email || "", username: t.username || "", password: "" }); setOpen(true); };

    const submit = async () => {
        if (!form.code?.trim()) return toast.error("Código es obligatorio");
        if (!form.full_name?.trim()) return toast.error("Nombre es obligatorio");
        try {
            const payload = { ...form };
            if (!payload.password) delete payload.password;
            if (editing) await api.put(`/technicians/${editing.id}`, payload);
            else await api.post("/technicians", payload);
            toast.success(editing ? "Actualizado" : "Creado");
            setOpen(false); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };

    const remove = async (t) => {
        if (!window.confirm(`Eliminar al técnico ${t.full_name}?`)) return;
        try { await api.delete(`/technicians/${t.id}`); toast.success("Eliminado"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };

    const uploadTechSignature = async (dataUri) => {
        const res = await api.post(`/technicians/${editing.id}/signature`, { data_uri: dataUri });
        setEditing((t) => ({ ...t, signature_url: res.data.signature_url }));
        load();
    };
    const removeTechSignature = async () => {
        await api.post(`/technicians/${editing.id}/signature`, { data_uri: null });
        setEditing((t) => ({ ...t, signature_url: null }));
        load();
    };

    const downloadTemplate = async () => {
        try {
            const res = await api.get("/technicians/import/template", { responseType: "blob" });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "plantilla_tecnicos_trulyn.xlsx";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error("Error al descargar plantilla");
        }
    };

    const handleImportFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImportFile(file);
        setImportLoading(true);
        setImportPreview(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await api.post("/technicians/import/preview", fd);
            setImportPreview(res.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error al procesar archivo");
        } finally { setImportLoading(false); }
        e.target.value = "";
    };

    const executeImport = async () => {
        if (!importFile) return;
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append("file", importFile);
            const res = await api.post("/technicians/import/execute", fd);
            const n = res.data.queued ?? res.data.created ?? 0;
            toast.success(`Importando ${n} técnicos… irán apareciendo en la lista en unos momentos.`);
            setShowImport(false);
            setImportPreview(null);
            setImportFile(null);
            // Background insertion (bcrypt is slow); refresh the list progressively.
            load();
            let ticks = 0;
            const poll = setInterval(() => {
                ticks += 1;
                load();
                if (ticks >= 18) clearInterval(poll); // ~90s
            }, 5000);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error al importar");
        } finally { setImporting(false); }
    };

    const filtered = items.filter((t) =>
        !q || t.full_name?.toLowerCase().includes(q.toLowerCase()) || t.code?.toLowerCase().includes(q.toLowerCase()) || t.email?.toLowerCase().includes(q.toLowerCase()));

    return (
        <div data-testid="admin-technicians">
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-semibold" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Técnicos</h1>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => { setShowImport(true); setImportPreview(null); setImportFile(null); }} data-testid="admin-techs-import">
                        <Upload className="w-4 h-4 mr-1" /> Importar Excel
                    </Button>
                    <Button onClick={openCreate} className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" data-testid="admin-techs-new">
                        <Plus className="w-4 h-4 mr-1" /> Nuevo técnico
                    </Button>
                </div>
            </div>
            <div className="relative max-w-sm mb-3">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, código o email" className="pl-9" data-testid="admin-techs-search" />
            </div>
            <Card data-testid="admin-technicians-table">
                <Table>
                    <TableHeader>
                        <TableRow><TableHead>Código</TableHead><TableHead>Nombre</TableHead><TableHead>Usuario</TableHead><TableHead>Contraseña</TableHead><TableHead>Tel.</TableHead><TableHead>Email</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow>
                    </TableHeader>
                    <TableBody>
                        {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">Sin técnicos</TableCell></TableRow>}
                        {filtered.map((t) => (
                            <TableRow key={t.id}>
                                <TableCell className="font-mono">{t.code}</TableCell>
                                <TableCell className="font-medium">{t.full_name}</TableCell>
                                <TableCell className="font-mono text-sm" data-testid={`tech-username-${t.id}`}>{t.username || "—"}</TableCell>
                                <TableCell className="font-mono text-sm">
                                    {t.initial_password ? (
                                        <div className="flex items-center gap-1.5">
                                            <span className={`px-1.5 py-0.5 rounded border ${revealPw[t.id] ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-muted text-muted-foreground border-transparent tracking-widest"}`} data-testid={`tech-password-${t.id}`}>
                                                {revealPw[t.id] ? t.initial_password : "••••••••"}
                                            </span>
                                            <button
                                                type="button"
                                                className="text-muted-foreground hover:text-foreground"
                                                title={revealPw[t.id] ? "Ocultar" : "Mostrar"}
                                                onClick={() => setRevealPw((s) => ({ ...s, [t.id]: !s[t.id] }))}
                                                data-testid={`tech-password-toggle-${t.id}`}
                                            >
                                                {revealPw[t.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                            <button
                                                type="button"
                                                className="text-muted-foreground hover:text-foreground"
                                                title="Copiar contraseña"
                                                onClick={() => { navigator.clipboard?.writeText(t.initial_password); toast.success("Contraseña copiada"); }}
                                                data-testid={`tech-password-copy-${t.id}`}
                                            >
                                                <Copy className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <span className="text-muted-foreground">••••••</span>
                                    )}
                                </TableCell>
                                <TableCell className="font-mono text-sm">{t.phone || "—"}</TableCell>
                                <TableCell className="text-sm">{t.email || "—"}</TableCell>
                                <TableCell>{t.is_active ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Activo</Badge> : <Badge variant="outline">Inactivo</Badge>}</TableCell>
                                <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1.5">
                                        <div className="flex items-center gap-1.5 mr-1" title={t.is_active ? "Desactivar acceso" : "Activar acceso"}>
                                            <Switch checked={t.is_active} onCheckedChange={() => toggleActive(t)} data-testid={`admin-techs-toggle-${t.id}`} />
                                        </div>
                                        <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`admin-techs-edit-${t.id}`}><Edit className="w-4 h-4" /></Button>
                                        <Button variant="ghost" size="icon" onClick={() => remove(t)} data-testid={`admin-techs-delete-${t.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>

            {/* CREATE/EDIT DIALOG */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{editing ? "Editar técnico" : "Nuevo técnico"}</DialogTitle></DialogHeader>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div><Label>Código *</Label><Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} data-testid="tech-form-code" /></div>
                        <div><Label>Nombre completo *</Label><Input value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} data-testid="tech-form-name" /></div>
                        <div><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="tech-form-phone" /></div>
                        <div><Label>Email (opcional)</Label><Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="tech-form-email" /></div>
                        <div><Label>Usuario (opcional)</Label><Input value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} placeholder="Se genera automáticamente" data-testid="tech-form-username" /></div>
                        <div className="sm:col-span-2"><Label>Contraseña (opcional)</Label><Input type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Si se deja vacía, el sistema genera una" data-testid="tech-form-password" /></div>
                        <div className="sm:col-span-2 flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} /> <span className="text-sm">Activo</span></div>
                        {editing && (
                            <div className="sm:col-span-2 pt-2 border-t">
                                <SignatureUpload
                                    signatureUrl={editing?.signature_url}
                                    onUpload={uploadTechSignature}
                                    onRemove={removeTechSignature}
                                    testid="tech-signature"
                                />
                            </div>
                        )}
                    </div>
                    <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={submit} className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" data-testid="tech-form-submit">{editing ? "Guardar" : "Crear"}</Button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* IMPORT DIALOG */}
            <Dialog open={showImport} onOpenChange={setShowImport}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileSpreadsheet className="w-5 h-5" /> Importar técnicos desde Excel
                        </DialogTitle>
                        <DialogDescription>
                            Suba un archivo Excel con los datos de los técnicos. Se pueden crear cuentas de usuario automáticamente.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <Button variant="outline" onClick={downloadTemplate} data-testid="tech-import-download-template">
                                <Download className="w-4 h-4 mr-1.5" /> Descargar plantilla
                            </Button>
                            <span className="text-xs text-muted-foreground">o</span>
                            <Button variant="outline" onClick={() => importRef.current?.click()} disabled={importLoading} data-testid="tech-import-upload-btn">
                                <Upload className="w-4 h-4 mr-1.5" /> {importLoading ? "Procesando..." : "Subir archivo"}
                            </Button>
                            <input ref={importRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImportFile} />
                        </div>

                        {importFile && !importLoading && (
                            <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                                <FileSpreadsheet className="w-3.5 h-3.5" />
                                {importFile.name}
                            </div>
                        )}

                        {importPreview && (
                            <div className="space-y-3">
                                <div className="flex gap-3">
                                    <div className="flex-1 rounded-lg border p-3 text-center">
                                        <div className="text-2xl font-bold">{importPreview.total}</div>
                                        <div className="text-xs text-muted-foreground">Total filas</div>
                                    </div>
                                    <div className="flex-1 rounded-lg border border-green-200 bg-green-50 p-3 text-center">
                                        <div className="text-2xl font-bold text-green-700">{importPreview.valid}</div>
                                        <div className="text-xs text-green-700">Válidos</div>
                                    </div>
                                    <div className={`flex-1 rounded-lg border p-3 text-center ${importPreview.invalid > 0 ? "border-red-200 bg-red-50" : "border-border"}`}>
                                        <div className={`text-2xl font-bold ${importPreview.invalid > 0 ? "text-red-700" : ""}`}>{importPreview.invalid}</div>
                                        <div className={`text-xs ${importPreview.invalid > 0 ? "text-red-700" : "text-muted-foreground"}`}>Con errores</div>
                                    </div>
                                </div>

                                <div className="max-h-[300px] overflow-y-auto rounded-lg border">
                                    <table className="w-full text-xs">
                                        <thead className="bg-secondary sticky top-0">
                                            <tr>
                                                <th className="px-2 py-1.5 text-left">Fila</th>
                                                <th className="px-2 py-1.5 text-left">Código</th>
                                                <th className="px-2 py-1.5 text-left">Nombre</th>
                                                <th className="px-2 py-1.5 text-left">Usuario</th>
                                                <th className="px-2 py-1.5 text-left">Email</th>
                                                <th className="px-2 py-1.5 text-left">Estado</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {importPreview.rows.map((r, i) => (
                                                <tr key={i} className={r.errors.length > 0 ? "bg-red-50" : ""}>
                                                    <td className="px-2 py-1.5 font-mono">{r.row}</td>
                                                    <td className="px-2 py-1.5 font-mono">{r.data.codigo}</td>
                                                    <td className="px-2 py-1.5">{r.data.nombre}</td>
                                                    <td className="px-2 py-1.5 font-mono">{r.data.usuario || <span className="text-muted-foreground italic">auto</span>}</td>
                                                    <td className="px-2 py-1.5">{r.data.email || "—"}</td>
                                                    <td className="px-2 py-1.5">
                                                        {r.errors.length === 0 ? (
                                                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                                                        ) : (
                                                            <div className="flex items-start gap-1">
                                                                <AlertTriangle className="w-3.5 h-3.5 text-red-600 shrink-0 mt-0.5" />
                                                                <div className="space-y-0.5">
                                                                    {r.errors.map((e, j) => <div key={j} className="text-red-600">{e}</div>)}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowImport(false)}>Cancelar</Button>
                        {importPreview && importPreview.invalid === 0 && importPreview.valid > 0 && (
                            <Button
                                onClick={executeImport}
                                disabled={importing}
                                className="bg-green-600 hover:bg-green-700 text-white font-semibold"
                                data-testid="tech-import-execute-btn"
                            >
                                {importing ? "Importando..." : `Importar ${importPreview.valid} técnicos`}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
